import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { RecruitingActions } from '../actions';
import { RecruitingHubMessage } from 'src/app/shared/services/realtime-constants';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { JobAlertRulesService } from 'src/app/recruiting/dashboard/job-alert-rules.service';
import { JobAlertDto } from 'src/app/shared/models/job-alert.model';
import { RecruitingDashboardAlert } from 'src/app/recruiting/dashboard/models/recruiting-dashboard-alert.model';
import { UserEventService } from '../../../shared/services/user-event.service';
import { UserEventTypeEnum } from '../../../shared/models/enums/user-event.enum';

@Injectable()
export class RecruitingDashboardEffects {
    private static readonly jobAlertsViewedPageName = 'notifications-view';

    constructor(
        private readonly _actions$: Actions,
        private readonly _hubConnectionService: HubConnectionService,
        private readonly _jobsService: JobsService,
        private readonly _jobAlertRulesService: JobAlertRulesService,
        private readonly _userEventService: UserEventService
    ) {}

    jobUpdated$ = createEffect(() => {
        return this._hubConnectionService.coreHub.messagesOf<JobAlertDto>(RecruitingHubMessage.JOBS_UPDATED).pipe(
            map((jobAlert) => RecruitingActions.Dashboard.jobUpdatedAlertsSuccess({ jobAlert })),
            catchError((error) => of(RecruitingActions.Dashboard.jobUpdatedAlertsFailure({ error })))
        );
    });

    leadAssigned$ = createEffect(() => {
        return this._hubConnectionService.coreHub.messagesOf<number>(RecruitingHubMessage.LEAD_ASSIGNED).pipe(
            map((message) => RecruitingActions.Dashboard.leadAssignedAlertSuccess({ message: message })),
            catchError((error) => of(RecruitingActions.Dashboard.leadAssignedAlertFailure({ error })))
        );
    });

    recruitingDashboardAlert$ = createEffect(() =>
        this._hubConnectionService.coreHub
            .messagesOf<RecruitingDashboardAlert>(RecruitingHubMessage.RECRUITING_DASHBOARD_ALERT)
            .pipe(
                map((data) => RecruitingActions.Dashboard.recruitingDashboardAlertSuccess({ data: data })),
                catchError((error) => of(RecruitingActions.Dashboard.recruitingDashboardAlertFailure({ error })))
            )
    );

    recruitingDashboardJobAlert$ = createEffect(() =>
        this._hubConnectionService.coreHub
            .messagesOf<JobAlertDto[]>(RecruitingHubMessage.RECRUITING_DASHBOARD_JOB_ALERT)
            .pipe(
                map((data) => RecruitingActions.Dashboard.recruitingDashboardJobAlertSuccess({ data: data })),
                catchError((error) => of(RecruitingActions.Dashboard.recruitingDashboardJobAlertFailure({ error })))
            )
    );

    getJobAlerts$ = createEffect(() =>
        this._actions$.pipe(
            ofType(RecruitingActions.Dashboard.jobAlertsForRecruiter),
            switchMap(() =>
                this._jobsService.getJobAlerts().pipe(
                    map((jobAlerts) => RecruitingActions.Dashboard.jobAlertsSuccess({ jobAlerts })),
                    catchError((error) => {
                        return of(RecruitingActions.Dashboard.jobAlertsFailure({ error }));
                    })
                )
            )
        )
    );

    newGetJobAlerts$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.Dashboard.recruiterDashboardViewed),
            switchMap(() =>
                this._jobsService.getJobAlerts().pipe(
                    map((jobAlerts) => RecruitingActions.Dashboard.jobAlertsSuccess({ jobAlerts: jobAlerts })),
                    catchError((error) => of(RecruitingActions.Dashboard.jobAlertsFailure({ error })))
                )
            )
        );
    });

    getJobAlertRules$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.Dashboard.recruiterDashboardViewed),
            switchMap(({ userId }: { userId: number }) =>
                this._jobAlertRulesService.loadJobAlertRules(userId).pipe(
                    map((results) => RecruitingActions.Dashboard.getJobAlertRulesSuccess({ jobAlertRules: results })),
                    catchError((error) => of(RecruitingActions.Dashboard.getJobAlertRulesFailure({ error })))
                )
            )
        );
    });

    getJobAlertRule$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                RecruitingActions.Dashboard.createJobAlertRuleSuccess,
                RecruitingActions.Dashboard.updateJobAlertRuleSuccess
            ),
            switchMap(({ userId, ruleName }: { userId: number; ruleName: string }) =>
                this._jobAlertRulesService.getJobAlertRule(userId, ruleName).pipe(
                    map((result) => RecruitingActions.Dashboard.getJobAlertRuleSuccess({ jobAlertRule: result })),
                    catchError((error) => of(RecruitingActions.Dashboard.getJobAlertRuleFailure({ error })))
                )
            )
        );
    });

    getLatestJobAlertsView$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                RecruitingActions.Dashboard.recruiterDashboardViewed,
                RecruitingActions.Dashboard.addJobAlertsViewEventSuccess
            ),
            switchMap(({ userId }: { userId: number }) =>
                this._userEventService
                    .getLatestUserEvent(
                        userId,
                        UserEventTypeEnum.JobAlertsView,
                        RecruitingDashboardEffects.jobAlertsViewedPageName
                    )
                    .pipe(
                        map((eventTimestamp) => {
                            const eventTs = new Date(eventTimestamp);
                            return RecruitingActions.Dashboard.getLatestJobAlertsViewSuccess({
                                eventTimestamp: eventTs
                            });
                        }),
                        catchError((error) => of(RecruitingActions.Dashboard.getLatestJobAlertsViewFailure({ error })))
                    )
            )
        );
    });

    jobAlertsViewOpened$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.Dashboard.recruiterJobAlertsViewOpened),
            switchMap(({ userId }: { userId: number }) =>
                this._userEventService
                    .addUserEvent({
                        userEventType: UserEventTypeEnum.JobAlertsView,
                        userId: userId,
                        eventObject: RecruitingDashboardEffects.jobAlertsViewedPageName
                    })
                    .pipe(
                        map(() => RecruitingActions.Dashboard.addJobAlertsViewEventSuccess({ userId })),
                        catchError((error) => of(RecruitingActions.Dashboard.addJobAlertsViewEventFailure({ error })))
                    )
            )
        );
    });
}
